import { get, map, flatMap, filter, some } from 'lodash';

import MasterProduct from 'models/MasterProduct';
import ParentCollection from 'models/ParentCollection';
import Product from 'models/Product';
import Filters, { FILTER_IDENTIFIERS } from 'utils/Filters';

import { STATUSES } from './constants';

const Catalog = class {
  /**
   * Returns a specific parent collection
   */
  static getParentCollection = (state, parentCollectionHandle) => {
    const data = get(state, ['catalog', 'parentCollections', parentCollectionHandle, 'data']);

    return data ? new ParentCollection(data) : null;
  };

  /**
   * Returns a specific master product
   */
  static getMasterProduct = (state, masterProductHandle) => {
    const data = get(state, ['catalog', 'masterProducts', masterProductHandle, 'data']);

    return data ? new MasterProduct(data) : null;
  };

  /**
   * Returns a specific colorway
   */
  static getColorway = (state, colorwayHandle) => {
    const data = get(state, ['catalog', 'colorways', colorwayHandle, 'data']);

    return data ? new Product(data) : null;
  };

  /**
   * Returns the colorways that belong to a master product
   */
  static getAllMasterProductColorways = (state, masterProductHandle) => {
    const masterProduct = Catalog.getMasterProduct(state, masterProductHandle);

    if (!masterProduct) {
      return null;
    }

    const colorwayHandles = get(masterProduct, 'colorways');

    return map(colorwayHandles, colorwayHandle => Catalog.getColorway(state, colorwayHandle));
  };

  /**
   * Returns the colorways that belong to a master product, and are available
   */
  static getAvailableMasterProductColorways = (state, masterProductHandle) => {
    const allColorways = Catalog.getAllMasterProductColorways(state, masterProductHandle);

    if (!allColorways) {
      return null;
    }

    const cacheKey = Catalog.getMasterProductCacheKey(state, masterProductHandle);

    const availableColorways = Catalog._filterByAvailability({ items: allColorways, cacheKey });

    return availableColorways;
  };

  /**
   * Returns the master products that belong to a parent collection
   */
  static getAllParentCollectionMasterProducts = (state, parentCollectionHandle) => {
    const parentCollection = Catalog.getParentCollection(state, parentCollectionHandle);

    if (!parentCollection) {
      return null;
    }

    const masterProductHandles = get(parentCollection, 'masterProducts');

    return map(masterProductHandles, masterProductHandle =>
      Catalog.getMasterProduct(state, masterProductHandle)
    ).filter(collection => collection);
  };

  /**
   * Returns the master products that belong to a parent collection, and are available
   */
  static getAvailableParentCollectionMasterProducts = (state, parentCollectionHandle) => {
    const parentCollection = Catalog.getParentCollection(state, parentCollectionHandle);

    if (!parentCollection) {
      return null;
    }

    const availableMasterProductHandles = filter(
      parentCollection.masterProducts,
      masterProductHandle => {
        const availableColorways = Catalog.getAvailableMasterProductColorways(
          state,
          masterProductHandle
        );

        return !!availableColorways.length;
      }
    );

    return map(availableMasterProductHandles, masterProductHandle =>
      Catalog.getMasterProduct(state, masterProductHandle)
    );
  };

  /**
   * Returns the colorways that belong to a parent collection
   */
  static getAllParentCollectionColorways = (state, parentCollectionHandle) => {
    const masterProducts = Catalog.getAllParentCollectionMasterProducts(
      state,
      parentCollectionHandle
    );

    if (!masterProducts) {
      return null;
    }

    const colorways = flatMap(masterProducts, ({ handle }) =>
      Catalog.getAllMasterProductColorways(state, handle)
    );

    return colorways;
  };

  /**
   * Returns the colorways that belong to a parent collection, and are available
   */
  static getAvailableParentCollectionColorways = (state, parentCollectionHandle) => {
    const allColorways = Catalog.getAllParentCollectionColorways(state, parentCollectionHandle);

    if (!allColorways) {
      return null;
    }

    const cacheKey = Catalog.getParentCollectionCacheKey(state, parentCollectionHandle);

    const availableColorways = Catalog._filterByAvailability({ items: allColorways, cacheKey });

    return availableColorways;
  };

  /**
   * Returns true when a parent collection is loading. Returns false otherwise.
   */
  static isParentCollectionLoading = (state, parentCollectionHandle) => {
    const parentCollection = Catalog.getParentCollection(state, parentCollectionHandle);

    if (!parentCollection) {
      return false;
    }

    return some(get(parentCollection, 'masterProducts'), masterProductHandle =>
      Catalog.isMasterProductLoading(state, masterProductHandle)
    );
  };

  /**
   * Returns true when a master product is loading. Returns false otherwise.
   */
  static isMasterProductLoading = (state, masterProductHandle) =>
    get(state, ['catalog', 'masterProducts', masterProductHandle, 'isLoading'], false);

  /**
   * Returns a cache key specific to a parent collection and whether it exists and is loaded. This does not take into account any filters
   */
  static getParentCollectionCacheKey = (state, parentCollectionHandle) => {
    const parentCollection = Catalog.getParentCollection(state, parentCollectionHandle);
    const isLoading = Catalog.isParentCollectionLoading(state, parentCollectionHandle);

    let status;
    if (!parentCollection) {
      status = STATUSES.MISSING;
    } else {
      status = isLoading ? STATUSES.LOADING : STATUSES.LOADED;
    }

    return `catalog__parent-collection__${parentCollectionHandle}__${status}`;
  };

  /**
   * Returns a cache key specific to a master product and whether it exists and is loaded.. This does not take into account any filters
   */
  static getMasterProductCacheKey = (state, masterProductHandle) => {
    const masterProduct = Catalog.getMasterProduct(state, masterProductHandle);
    const isLoading = Catalog.isMasterProductLoading(state, masterProductHandle);

    let status;
    if (!masterProduct) {
      status = STATUSES.MISSING;
    } else {
      status = isLoading ? STATUSES.LOADING : STATUSES.LOADED;
    }

    return `catalog__master-product__${masterProductHandle}__${status}`;
  };

  static getFirstMatchingFilterLabel = (state, filterValue) => {
    const colorways = Catalog.getColorways(state);

    if (!colorways) {
      return null;
    }

    const product = colorways.find(colorway => colorway.sizes[filterValue]);

    if (!product) {
      return null;
    }

    return product.sizes[filterValue].value;
  };

  static getColorways = state => {
    return Object.keys(state.catalog.colorways).map(handle => Catalog.getColorway(state, handle));
  };

  static _filterByAvailability = ({ items, cacheKey }) => {
    const filterItems = [
      {
        configuration: Filters.getFilterConfiguration({
          filterIdentifier: FILTER_IDENTIFIERS.AVAILABILITY,
        }),
        values: [true],
      },
    ];

    const availableItems = Filters.executeFilters({
      items,
      filterItems,
      cacheKey,
    });

    return availableItems;
  };
};

export default Catalog;
